import React from 'react';
import { useRealtorContext } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FileUpload from '../components/ui/FileUpload';
import FormInput from '../components/ui/FormInput';
import FormSelect from '../components/ui/FormSelect';
import FormTextarea from '../components/ui/FormTextarea';

const FileUploadPage: React.FC = () => {
  const { currentUser, loading, error } = useRealtorContext();
  
  // File upload state
  const [files, setFiles] = React.useState<{
    propertyDocuments: File[] | null;
    marketingMaterials: File[] | null;
    clientDocuments: File[] | null;
    otherFiles: File[] | null;
  }>({
    propertyDocuments: null,
    marketingMaterials: null,
    clientDocuments: null,
    otherFiles: null
  });
  
  // Form state
  const [formData, setFormData] = React.useState({
    title: '',
    category: '',
    description: '',
    tags: '',
    isPublic: false
  });
  
  // Form errors
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  
  // Success message
  const [showSuccess, setShowSuccess] = React.useState(false);
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle file change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, category: keyof typeof files) => {
    const uploadedFiles = e.target.files;
    if (uploadedFiles && uploadedFiles.length > 0) {
      const fileArray = Array.from(uploadedFiles);
      setFiles(prev => ({
        ...prev,
        [category]: fileArray
      }));
      
      // Clear error
      if (errors[category]) {
        setErrors(prev => ({
          ...prev,
          [category]: ''
        }));
      }
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    // Check if at least one file is uploaded
    const hasFiles = Object.values(files).some(fileList => fileList && fileList.length > 0);
    if (!hasFiles) {
      newErrors.files = 'At least one file must be uploaded';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Form is valid, upload files
    try {
      // In a real app, this would be an API call to upload files
      console.log('Uploading files:', files);
      console.log('Form data:', formData);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          title: '',
          category: '',
          description: '',
          tags: '',
          isPublic: false
        });
        setFiles({
          propertyDocuments: null,
          marketingMaterials: null,
          clientDocuments: null,
          otherFiles: null
        });
        
        // Reset file input elements
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach((input: any) => {
          input.value = '';
        });
      }, 3000);
    } catch (err) {
      console.error('Failed to upload files:', err);
    }
  };
  
  // Category options
  const categoryOptions = [
    { value: 'property', label: 'Property Documents' },
    { value: 'marketing', label: 'Marketing Materials' },
    { value: 'client', label: 'Client Documents' },
    { value: 'financial', label: 'Financial Records' },
    { value: 'legal', label: 'Legal Documents' },
    { value: 'other', label: 'Other' }
  ];
  
  // Get file count
  const getFileCount = () => {
    return Object.values(files).reduce((count, fileList) => {
      return count + (fileList ? fileList.length : 0);
    }, 0);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <div className="bg-primary-dark text-white py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">Document Upload Center</h1>
            <p className="text-xl max-w-2xl mx-auto">
              Upload, organize, and share important documents related to properties, clients, and marketing materials.
            </p>
          </div>
        </div>
        
        <Section background="light">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <Card className="p-6 mb-6">
                <h2 className="text-xl font-bold text-primary mb-4">Upload Guidelines</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 text-2xl text-secondary mr-3">•</div>
                    <div>
                      <p className="text-gray-700">
                        Maximum file size: <strong>25MB</strong> per file
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 text-2xl text-secondary mr-3">•</div>
                    <div>
                      <p className="text-gray-700">
                        Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, ZIP
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 text-2xl text-secondary mr-3">•</div>
                    <div>
                      <p className="text-gray-700">
                        Add descriptive titles and tags to make documents easily searchable
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 text-2xl text-secondary mr-3">•</div>
                    <div>
                      <p className="text-gray-700">
                        Set visibility to control who can access your documents
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h2 className="text-xl font-bold text-primary mb-4">Document Categories</h2>
                
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-all">
                    <h3 className="font-semibold text-primary mb-1">Property Documents</h3>
                    <p className="text-sm text-gray-600">
                      Floor plans, property photos, certificates, deeds
                    </p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-all">
                    <h3 className="font-semibold text-primary mb-1">Marketing Materials</h3>
                    <p className="text-sm text-gray-600">
                      Brochures, presentations, promotional videos
                    </p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-all">
                    <h3 className="font-semibold text-primary mb-1">Client Documents</h3>
                    <p className="text-sm text-gray-600">
                      Contracts, agreements, client information
                    </p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-all">
                    <h3 className="font-semibold text-primary mb-1">Financial Records</h3>
                    <p className="text-sm text-gray-600">
                      Commission statements, payment receipts
                    </p>
                  </div>
                </div>
              </Card>
            </div>
            
            <div className="lg:col-span-2">
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-primary mb-6">Upload Documents</h2>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
                    {error}
                  </div>
                )}
                
                {showSuccess && (
                  <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg animate-fade-in">
                    Files uploaded successfully!
                  </div>
                )}
                
                {errors.files && (
                  <div className="mb-6 p-4 rounded-lg" style={{ background: '#fad826', color: 'black' }}>
                    {errors.files}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormInput 
                      label="Document Title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter a descriptive title"
                      required
                      error={errors.title}
                    />
                    
                    <FormSelect 
                      label="Category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      options={categoryOptions}
                      placeholder="Select document category"
                      required
                      error={errors.category}
                    />
                  </div>
                  
                  <div className="mb-6">
                    <FormTextarea 
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Enter a brief description of the document(s)"
                      rows={3}
                    />
                  </div>
                  
                  <div className="mb-6">
                    <FormInput 
                      label="Tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleChange}
                      placeholder="Enter tags separated by commas (e.g., contract, 2-bedroom, bungalow)"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-start mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="isPublic"
                          name="isPublic"
                          type="checkbox"
                          checked={formData.isPublic}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="isPublic" className="font-medium text-gray-700">
                          Make documents visible to all team members
                        </label>
                        <p className="text-gray-500">
                          If unchecked, documents will only be visible to you and administrators.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6 mb-8">
                    <FileUpload 
                      label="Property Documents"
                      name="propertyDocuments"
                      onChange={(e) => handleFileChange(e, 'propertyDocuments')}
                      accept=".pdf,.doc,.docx,.jpg,.png,.zip"
                      multiple
                      helperText="Upload floor plans, property photos, certificates, etc."
                    />
                    
                    <FileUpload 
                      label="Marketing Materials"
                      name="marketingMaterials"
                      onChange={(e) => handleFileChange(e, 'marketingMaterials')}
                      accept=".pdf,.doc,.docx,.jpg,.png,.ppt,.pptx,.mp4"
                      multiple
                      helperText="Upload brochures, presentations, promotional materials, etc."
                    />
                    
                    <FileUpload 
                      label="Client Documents"
                      name="clientDocuments"
                      onChange={(e) => handleFileChange(e, 'clientDocuments')}
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      multiple
                      helperText="Upload contracts, agreements, client information, etc."
                    />
                    
                    <FileUpload 
                      label="Other Files"
                      name="otherFiles"
                      onChange={(e) => handleFileChange(e, 'otherFiles')}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png,.zip"
                      multiple
                      helperText="Upload any other relevant files"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-gray-600">
                      {getFileCount()} file(s) selected
                    </div>
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full md:w-auto"
                    >
                      {loading ? 'Uploading...' : 'Upload Documents'}
                    </Button>
                  </div>
                </form>
              </Card>
              
              <div className="mt-8 bg-primary-dark/5 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-primary mb-4">Recently Uploaded Documents</h3>
                
                <div className="space-y-4">
                  {currentUser ? (
                    <>
                      <div className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white transition-all">
                        <div className="flex-shrink-0 text-2xl text-red-500 mr-3">
                          <span role="img" aria-label="PDF">📄</span>
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-medium text-gray-900">3 Bedroom Bungalow - Floor Plan</h4>
                          <p className="text-sm text-gray-500">Uploaded 2 days ago • Property Documents</p>
                        </div>
                        <div className="flex-shrink-0">
                          <button className="text-primary hover:text-primary-dark">
                            View
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white transition-all">
                        <div className="flex-shrink-0 text-2xl text-blue-500 mr-3">
                          <span role="img" aria-label="Document">📋</span>
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-medium text-gray-900">Sales Contract Template</h4>
                          <p className="text-sm text-gray-500">Uploaded 3 days ago • Client Documents</p>
                        </div>
                        <div className="flex-shrink-0">
                          <button className="text-primary hover:text-primary-dark">
                            View
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white transition-all">
                        <div className="flex-shrink-0 text-2xl text-green-500 mr-3">
                          <span role="img" aria-label="Spreadsheet">📊</span>
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-medium text-gray-900">Q2 Commission Report</h4>
                          <p className="text-sm text-gray-500">Uploaded 1 week ago • Financial Records</p>
                        </div>
                        <div className="flex-shrink-0">
                          <button className="text-primary hover:text-primary-dark">
                            View
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-gray-500">
                      No documents found. Upload your first document to see it here.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FileUploadPage;
